import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  Skeleton,
  Alert,
  TablePagination,
  LinearProgress,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { useRentals } from '../../hooks/useRentals'
import { formatDate, formatNumber, formatCurrency } from '../../utils/formatters'

const RentalManagement = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRental, setSelectedRental] = useState(null)

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('')

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(0) // Reset to first page on search
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const { data, isLoading, error, refetch } = useRentals({
    page: page + 1,
    limit: rowsPerPage,
    search: debouncedSearch,
    status: statusFilter,
  })

  const rentals = data?.rentals || []
  const pagination = data?.pagination || {}
  const stats = data?.stats || {}

  const handleMenuClick = (event, rental) => {
    setAnchorEl(event.currentTarget)
    setSelectedRental(rental)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedRental(null)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
    setPage(0)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'primary'
      case 'completed':
        return 'success'
      case 'expired':
        return 'error'
      case 'cancelled':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif'
      case 'completed':
        return 'Tamamlandi'
      case 'expired':
        return 'Suresi Doldu'
      case 'cancelled':
        return 'Iptal Edildi'
      default:
        return 'Bilinmiyor'
    }
  }

  // Stats card component
  const StatsCard = ({ value, label, color, loading }) => (
    <Card>
      <CardContent>
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="80%" />
          </>
        ) : (
          <>
            <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main` }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  )

  if (error) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Kiralama Yonetimi
          </Typography>
        </Box>
        <Alert severity="error">
          Kiralama verileri yuklenirken hata olustu: {error.message}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Kiralama Yonetimi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              SMS kiralama islemlerini goruntuleyin ve yonetin
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatNumber(stats.active || 0)}
            label="Aktif Kiralama"
            color="primary"
            loading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatNumber(stats.completed || 0)}
            label="Tamamlanan"
            color="success"
            loading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatNumber(stats.expired || 0)}
            label="Suresi Dolan"
            color="error"
            loading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatCurrency(stats.total_credits_spent || 0)}
            label="Toplam Harcama"
            color="info"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Kullanici email veya telefon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">Tumu</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="completed">Tamamlandi</MenuItem>
                  <MenuItem value="expired">Suresi Doldu</MenuItem>
                  <MenuItem value="cancelled">Iptal Edildi</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
              >
                Gelismis Filtre
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rentals Table */}
      <Card>
        {isLoading && <LinearProgress />}
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Kiralama Listesi ({formatNumber(pagination.total || 0)})
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanici</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Servis</TableCell>
                  <TableCell>Ulke</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Maliyet</TableCell>
                  <TableCell>Baslangic</TableCell>
                  <TableCell>SMS</TableCell>
                  <TableCell align="center">Islemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  // Loading skeleton rows
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                    </TableRow>
                  ))
                ) : rentals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        Kiralama bulunamadi
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rentals.map((rental) => (
                    <TableRow key={rental.id} hover>
                      <TableCell>{rental.user_email || '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {rental.phone_number || '-'}
                      </TableCell>
                      <TableCell>{rental.service_name || '-'}</TableCell>
                      <TableCell>{rental.country_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(rental.status)}
                          color={getStatusColor(rental.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(rental.cost_in_credits || 0)}</TableCell>
                      <TableCell>
                        {rental.created_at ? formatDate(rental.created_at, 'dd/MM/yyyy HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        {rental.sms_content ? (
                          <Chip
                            label="SMS Var"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="SMS Yok"
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, rental)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Sayfa basina:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
            }
          />
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuList>
          <MenuItemComponent onClick={handleMenuClose}>
            <ViewIcon sx={{ mr: 1 }} />
            Detaylari Goruntulle
          </MenuItemComponent>
          <MenuItemComponent onClick={handleMenuClose}>
            <EditIcon sx={{ mr: 1 }} />
            Duzenle
          </MenuItemComponent>
          <MenuItemComponent onClick={handleMenuClose} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Iptal Et
          </MenuItemComponent>
        </MenuList>
      </Menu>
    </Box>
  )
}

export default RentalManagement
